import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import Button from "../../../components/ui/Button";
import { DollarSign, Users, TrendingUp, ArrowRight, Star } from "lucide-react";

const FeatureCard = ({ icon: Icon, title, description, color }) => (
  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center text-center">
    <div className={`w-14 h-14 rounded-full ${color} flex items-center justify-center mb-4`}>
      <Icon size={24} className="text-white" />
    </div>
    <h3 className="font-bold text-slate-800 mb-2">{title}</h3>
    <p className="text-sm text-slate-500">{description}</p>
  </div>
);

const CreatorSetupWizard = () => {
  const navigate = useNavigate();
  const { activateCreator } = useAuth();
  const [isActivating, setIsActivating] = useState(false);

  const handleActivate = () => {
    setIsActivating(true);
    // Simulate a brief activation process
    setTimeout(() => {
      activateCreator();
      navigate("/dashboard");
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Hero Section */}
      <div className="bg-white pb-12 pt-8 px-6 rounded-b-[3rem] shadow-sm">
        <div className="max-w-md mx-auto text-center">
          <div className="inline-flex items-center justify-center p-2 bg-purple-50 rounded-full mb-6">
            <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full mr-2">NEW</span>
            <span className="text-xs font-medium text-purple-700 pr-2">Join 10,000+ Creators</span>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 mb-4 leading-tight">
            Turn Your Passion Into <span className="text-primary">Profit</span>
          </h1>
          <p className="text-slate-500 mb-8 text-lg">
            Unlock subscriptions, sell merchandise, and build a thriving community on Adnaki.
          </p>
          
          <Button 
            size="lg" 
            className="w-full shadow-xl shadow-primary/20 py-4 text-lg" 
            onClick={handleActivate}
            disabled={isActivating}
          >
            {isActivating ? (
              <span className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Setting up your studio...
              </span>
            ) : (
              <span className="flex items-center">
                Become a Creator <ArrowRight size={20} className="ml-2" />
              </span>
            )}
          </Button>
          <p className="text-xs text-slate-400 mt-4">
            No credit card required. Free to start.
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="flex-grow px-6 py-8">
        <div className="max-w-md mx-auto space-y-4">
          <FeatureCard 
            icon={DollarSign}
            title="Multiple Income Streams"
            description="Subscriptions, tips, paid posts, and merchandise sales all in one place."
            color="bg-green-500"
          />
          <FeatureCard 
            icon={Users}
            title="Own Your Audience"
            description="Direct access to your fans. No algorithm hiding your content."
            color="bg-blue-500"
          />
          <FeatureCard 
            icon={TrendingUp}
            title="Powerful Analytics"
            description="Track your growth and understand what your fans love most."
            color="bg-purple-500"
          />
        </div>
      </div>

      {/* Testimonial / Social Proof */}
      <div className="px-6 pb-12">
        <div className="max-w-md mx-auto bg-slate-800 rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
          <div className="flex items-center space-x-1 mb-4 text-yellow-400">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} size={16} fill="currentColor" />)}
          </div>
          <p className="text-lg font-medium mb-4">"Adnaki changed my life. I went from 0 to earning a full-time income in just 3 months."</p>
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center font-bold text-sm">GK</div>
            <div className="ml-3">
              <p className="font-bold text-sm">Gigi Kiya</p>
              <p className="text-xs text-slate-400">Musician & Creator</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorSetupWizard;
